import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DashboardKpis } from '../types';
import './DashboardOverview.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface DashboardOverviewProps {
  authToken: string;
  onNavigate: (tab: string) => void;
}

const AKTION_LABEL: Record<string, string> = {
  CREATE:       'Erstellt',
  UPDATE:       'Geändert',
  DEACTIVATE:   'Deaktiviert',
  ACTIVATE:     'Aktiviert',
  AGENT_AKTION: 'Agent',
};

const AKTION_COLOR: Record<string, string> = {
  CREATE:       'act-green',
  UPDATE:       'act-blue',
  DEACTIVATE:   'act-orange',
  ACTIVATE:     'act-teal',
  AGENT_AKTION: 'act-purple',
};

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatCurrency(val: number): string {
  return val.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
}

const DashboardOverview: React.FC<DashboardOverviewProps> = ({ authToken, onNavigate }) => {
  const [kpis, setKpis] = useState<DashboardKpis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const res = await axios.get(`${API_URL}/api/dashboard/kpis`, { headers });
      setKpis(res.data);
    } catch (e: any) {
      setError('Daten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <div className="dashboard-loading">Lade Dashboard...</div>;
  }

  if (error || !kpis) {
    return <div className="dashboard-error">{error || 'Keine Daten'}</div>;
  }

  const warnAccent =
    kpis.warnungen.hoch > 0   ? 'accent-red'    :
    kpis.warnungen.mittel > 0 ? 'accent-orange'  : 'accent-green';

  const forderAccent = kpis.forderungen.ueberfaellig > 0 ? 'accent-orange' : '';
  const auftragAccent = kpis.auftraege.ueberfaellig > 0  ? 'accent-orange' : '';
  const lagerAccent  = kpis.lager.alarme > 0             ? 'accent-red'    : 'accent-green';

  return (
    <div className="dashboard-overview">
      <div className="dashboard-header">
        <h2>Dashboard</h2>
        <button className="btn-refresh" onClick={load} title="Aktualisieren">↻ Aktualisieren</button>
      </div>

      {/* KPI-Karten */}
      <div className="kpi-grid">

        {/* Warnungen */}
        <div className={`kpi-card ${warnAccent}`} onClick={() => onNavigate('warnungen')}>
          <div className="kpi-icon">01</div>
          <div className="kpi-body">
            <div className="kpi-title">Aktive Warnungen</div>
            <div className="kpi-value">{kpis.warnungen.gesamt}</div>
            <div className="kpi-sub">
              {kpis.warnungen.hoch > 0 && <span className="sub-badge red">HOCH: {kpis.warnungen.hoch}</span>}
              {kpis.warnungen.mittel > 0 && <span className="sub-badge orange">MITTEL: {kpis.warnungen.mittel}</span>}
              {kpis.warnungen.gesamt === 0 && <span className="sub-ok">Alles in Ordnung</span>}
            </div>
          </div>
        </div>

        {/* Offene Aufträge */}
        <div className={`kpi-card ${auftragAccent}`} onClick={() => onNavigate('auftraege')}>
          <div className="kpi-icon">02</div>
          <div className="kpi-body">
            <div className="kpi-title">Offene Aufträge</div>
            <div className="kpi-value">{kpis.auftraege.offen}</div>
            <div className="kpi-sub">
              {kpis.auftraege.ueberfaellig > 0
                ? <span className="sub-badge orange">{kpis.auftraege.ueberfaellig} überfällig</span>
                : <span className="sub-ok">Keine überfällig</span>
              }
            </div>
          </div>
        </div>

        {/* Offene Forderungen */}
        <div className={`kpi-card ${forderAccent}`} onClick={() => onNavigate('dokumente')}>
          <div className="kpi-icon">03</div>
          <div className="kpi-body">
            <div className="kpi-title">Offene Forderungen</div>
            <div className="kpi-value">{formatCurrency(kpis.forderungen.summe)}</div>
            <div className="kpi-sub">
              <span>{kpis.forderungen.anzahl} Rechnung{kpis.forderungen.anzahl !== 1 ? 'en' : ''}</span>
              {kpis.forderungen.ueberfaellig > 0 &&
                <span className="sub-badge orange"> · {kpis.forderungen.ueberfaellig} überfällig</span>
              }
            </div>
          </div>
        </div>

        {/* Fertigung */}
        <div className="kpi-card" onClick={() => onNavigate('fertigung')}>
          <div className="kpi-icon">04</div>
          <div className="kpi-body">
            <div className="kpi-title">Aktive Fertigung</div>
            <div className="kpi-value">{kpis.fertigung.aktiv}</div>
            <div className="kpi-sub">
              {kpis.fertigung.fortschritt_avg !== null
                ? <span>Ø Fortschritt: {kpis.fertigung.fortschritt_avg}%</span>
                : <span className="sub-ok">Kein aktiver FA</span>
              }
            </div>
          </div>
        </div>

        {/* Bestellungen */}
        <div className="kpi-card" onClick={() => onNavigate('bestellungen')}>
          <div className="kpi-icon">05</div>
          <div className="kpi-body">
            <div className="kpi-title">Offene Bestellungen</div>
            <div className="kpi-value">{kpis.bestellungen.offen}</div>
            <div className="kpi-sub">
              <span>{kpis.verbindlichkeiten.anzahl} fällige Eingangsrechnungen</span>
            </div>
          </div>
        </div>

        {/* Lageralarme */}
        <div className={`kpi-card ${lagerAccent}`} onClick={() => onNavigate('lager')}>
          <div className="kpi-icon">06</div>
          <div className="kpi-body">
            <div className="kpi-title">Lageralarme</div>
            <div className="kpi-value">{kpis.lager.alarme}</div>
            <div className="kpi-sub">
              {kpis.lager.alarme > 0
                ? <span className="sub-badge red">Mindestbestand unterschritten</span>
                : <span className="sub-ok">Alle Bestände ausreichend</span>
              }
            </div>
          </div>
        </div>

      </div>

      {/* Letzte Aktivitäten */}
      <div className="aktivitaeten-section">
        <h3>Letzte Aktivitäten</h3>
        {kpis.letzte_aktivitaeten.length === 0 ? (
          <div className="aktivitaeten-empty">Noch keine Aktivitäten protokolliert.</div>
        ) : (
          <table className="aktivitaeten-table">
            <thead>
              <tr>
                <th>Zeitstempel</th>
                <th>Benutzer</th>
                <th>Aktion</th>
                <th>Tabelle</th>
                <th>ID</th>
              </tr>
            </thead>
            <tbody>
              {kpis.letzte_aktivitaeten.map((a, i) => (
                <tr key={i}>
                  <td className="col-ts">{formatTime(a.zeitstempel)}</td>
                  <td>{a.benutzer}</td>
                  <td>
                    <span className={`act-badge ${AKTION_COLOR[a.aktion] || ''}`}>
                      {AKTION_LABEL[a.aktion] || a.aktion}
                    </span>
                  </td>
                  <td className="col-tabelle">{a.tabelle}</td>
                  <td className="col-id">{a.datensatz_id ?? '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default DashboardOverview;
